import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirm } from '@munifocus/ui';

export interface ConfirmActionOptions {
  title: string;
  /** Name the record and the consequence, not just "are you sure". */
  body: string;
  confirmLabel: string;
  /** `danger` for anything that removes access, destroys data, or cannot be undone. */
  tone?: 'danger' | 'default';
}

/**
 * "Are you sure?" with the shared cancel label already filled in.
 *
 * A thin wrapper over {@link useConfirm} so a call site names only what differs
 * — the question, the consequence and the verb. Every extra argument a guard
 * needs is a reason somebody skips adding it, which is how suspending a company
 * and revoking a citizen's file access ended up firing straight off a button.
 *
 *   const confirm = useConfirmAction();
 *   if (!(await confirm({ title, body, confirmLabel, tone: 'danger' }))) return;
 */
export function useConfirmAction() {
  const { t } = useTranslation();
  const confirm = useConfirm();
  return useCallback(
    (options: ConfirmActionOptions) =>
      confirm({ ...options, cancelLabel: t('common.actions.cancel') }),
    [confirm, t],
  );
}
